/**
 * Camera and microphone choices for the local tile, folded behind one gear.
 *
 * The choices used to sit permanently on top of the self-view, covering the
 * signer's own hands. Now the tile stays clear: a 44 px gear in its corner
 * opens a popover toward whichever side of the stage has room, and Escape or
 * a press outside closes it and hands focus back to the gear.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconGear } from '../../../base/icons/svg';
export const DEVICE_SETTINGS_POPOVER_ID = 'vrs-self-view-device-settings';
const GEAR_SIZE = 44;
const GAP = 8;
const MIN_POPOVER_HEIGHT = GEAR_SIZE * 2;
// Bands the self-view already keeps clear (useSelfViewPosition): the top info
// bar and the toolbar. The popover stays out of them too.
const STAGE_TOP_INSET = 56;
const STAGE_BOTTOM_INSET = 88;
/**
 * Where the popover sits relative to the tile that holds the gear.
 *
 * It opens downward from under the gear when there is at least as much room
 * below as above the tile, otherwise it opens above the tile. It lines up with
 * the tile edge nearest the stage edge so a corner self-view never pushes it
 * off screen. Without measurements it falls back to "below the gear".
 *
 * @param {IBox} [stage] - Stage bounds.
 * @param {IBox} [tile] - Bounds of the tile holding the gear.
 * @returns {CSSProperties} Offsets and max height for the popover.
 */
export function getDevicePopoverPlacement(stage, tile) {
    if (!stage || !tile) {
        return { left: 0, top: GEAR_SIZE + GAP };
    }
    const onRightHalf = tile.left + (tile.width / 2) > stage.left + (stage.width / 2);
    const horizontal = onRightHalf ? { right: 0 } : { left: 0 };
    const roomBelow = stage.bottom - STAGE_BOTTOM_INSET - (tile.top + GEAR_SIZE + GAP);
    const roomAbove = tile.top - GAP - (stage.top + STAGE_TOP_INSET);
    return roomBelow >= roomAbove
        ? { ...horizontal, maxHeight: Math.max(roomBelow, MIN_POPOVER_HEIGHT), top: GEAR_SIZE + GAP }
        : { ...horizontal, bottom: `calc(100% + ${GAP}px)`, maxHeight: Math.max(roomAbove, MIN_POPOVER_HEIGHT) };
}
const useStyles = makeStyles()(theme => ({
    gear: {
        alignItems: 'center',
        background: theme.palette.ui01,
        border: 0,
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        display: 'flex',
        height: GEAR_SIZE,
        justifyContent: 'center',
        left: 0,
        padding: 0,
        position: 'absolute',
        top: 0,
        width: GEAR_SIZE,
        zIndex: 7,
        '&[aria-expanded="true"]': {
            background: theme.palette.ui03
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.focus01}`,
            outlineOffset: -2
        }
    },
    popover: {
        background: theme.palette.ui01,
        border: `1px solid ${theme.palette.ui04}`,
        borderRadius: 12,
        boxShadow: '0 14px 36px rgba(0, 0, 0, 0.32)',
        boxSizing: 'border-box',
        display: 'grid',
        gap: theme.spacing(1),
        overflowY: 'auto',
        padding: theme.spacing(1),
        position: 'absolute',
        width: 'min(292px, calc(100vw - 32px))',
        zIndex: 8,
        // `display: grid` above would otherwise override the hidden attribute.
        '&[hidden]': {
            display: 'none'
        }
    }
}));
const SelfViewDeviceSettings = ({ children, label, onOpenChange, open, stageRef }) => {
    const { classes, theme } = useStyles();
    const gearRef = useRef(null);
    const popoverRef = useRef(null);
    const [placement, setPlacement] = useState(() => getDevicePopoverPlacement());
    const closeAndFocusGear = useCallback(() => {
        onOpenChange(false);
        gearRef.current?.focus();
    }, [onOpenChange]);
    const onGearClick = useCallback(() => onOpenChange(!open), [onOpenChange, open]);
    const onKeyDown = useCallback((event) => {
        if (!open || event.key !== 'Escape') {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        closeAndFocusGear();
    }, [closeAndFocusGear, open]);
    // Re-measure after every render while open: the tile can be moved with
    // the arrow keys or the window resized without the popover closing.
    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        const next = getDevicePopoverPlacement(stageRef.current?.getBoundingClientRect(), gearRef.current?.offsetParent?.getBoundingClientRect());
        setPlacement(current => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    });
    useEffect(() => {
        if (!open) {
            return;
        }
        const onPointerDown = (event) => {
            const target = event.target;
            if (target && (gearRef.current?.contains(target) || popoverRef.current?.contains(target))) {
                return;
            }
            onOpenChange(false);
            // Hand focus back to the gear unless the press focused something
            // else (a toolbar button, the chat box).
            window.setTimeout(() => {
                const active = document.activeElement;
                if (!active || active === document.body || popoverRef.current?.contains(active)) {
                    gearRef.current?.focus();
                }
            });
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [onOpenChange, open]);
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { "aria-controls": DEVICE_SETTINGS_POPOVER_ID, "aria-expanded": open, "aria-label": label, className: classes.gear, onClick: onGearClick, onKeyDown: onKeyDown, ref: gearRef, title: label, type: 'button' },
            React.createElement(Icon, { color: theme.palette.icon01, size: 22, src: IconGear })),
        React.createElement("div", { "aria-label": label, className: classes.popover, hidden: !open, id: DEVICE_SETTINGS_POPOVER_ID, onKeyDown: onKeyDown, ref: popoverRef, role: 'group', style: placement }, children)));
};
export default SelfViewDeviceSettings;
